import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  InputBase,
  FormControl,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { setSearchParamsAction } from "../../store/reducers/mmymSlice";
import { ClearOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { searchDropdownOptions, searchTypeList } from "./searchUtils";

const SearchBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: "2px solid #dbdbdb",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
}));

const StyledFormControl = styled(FormControl)({
  marginRight: 8,
  minWidth: 120,
});

const StyledInputBase = styled(InputBase)({
  marginLeft: 8,
  flex: 1,
  height: 36,
});

type SearchType =
  | "NEW LEAD"
  | "ESTIMATES"
  | "SURVEY"
  | "COMPLETED"
  | "QUOTE"
  | "JOB"
  | "CONFIRMED"
  | "ARCHIVED";

interface CustomSearchBarProps {
  value?: string;
}

export default function CustomSearchBar({ value }: CustomSearchBarProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { searchParams } = useSelector((state: any) => state.mmym);
  const { user } = useSelector((state: any) => state?.user);

  const [searchValue, setSearchValue] = useState(searchParams?.searchString || "");
  const latestSearchParamsRef = useRef(searchParams);

  const isSurveyor = user?.user?.roles?.includes("Surveyor");

  // dropdown change
  const handleSearchTypeChange = (event: SelectChangeEvent<SearchType>) => {
    dispatch(
      setSearchParamsAction({
        ...searchParams,
        filterStatus: event.target.value,
      })
    );

    navigate(searchTypeList[event.target.value]?.page);
  };

  const debouncedSearchDispatch = useMemo(
    () =>
      debounce((searchString: string) => {
        dispatch(
          setSearchParamsAction({
            ...latestSearchParamsRef.current,
            searchString,
          })
        );
      }, 500),
    [dispatch]
  );

  // search input change
  const handleSearchKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSearchValue = event.target.value;
    setSearchValue(nextSearchValue);
    debouncedSearchDispatch(nextSearchValue);
  };

  // clear search
  const hideHandler = () => {
    debouncedSearchDispatch.cancel();
    setSearchValue("");
    dispatch(
      setSearchParamsAction({
        ...searchParams,
        searchString: "",
        filterStatus: "CURRENT",
      })
    );
  };

  useEffect(() => {
    latestSearchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    setSearchValue(searchParams?.searchString || "");
  }, [searchParams?.searchString]);

  useEffect(() => {
    return () => {
      debouncedSearchDispatch.cancel();
    };
  }, [debouncedSearchDispatch]);

  // page change reset
  useEffect(() => {
    let roleType = isSurveyor ? "Surveyor" : "";

    if (location.pathname !== searchParams?.page) {
      dispatch(
        setSearchParamsAction({
          page: location.pathname,
          searchString: "",
          filterStatus: "",
          searchOption: roleType === "Surveyor" ? "surveyLead" : "lead",
        })
      );
      setSearchValue("");
    }
  }, [location.pathname]);

  // ✅ Default PENDING for Surveyor
  useEffect(() => {
    if (value === "1" && isSurveyor && !searchParams?.filterStatus) {
      dispatch(
        setSearchParamsAction({
          ...searchParams,
          filterStatus: "PENDING",
        })
      );
    }
  }, [value, isSurveyor]);

  return location.pathname === searchParams?.page &&
    searchParams?.page === "/dashboard" ? (
    <SearchBar
      sx={{
        display: { xs: "flex", md: "flex" },
        height: "36px",
        position: "relative",
      }}
    >
      <StyledFormControl variant="filled" size="small">
        <Select
          sx={{
            "&:focus": { border: "0px" },
            "&, &:before, &:hover, .MuiFilledInput-input": {
              borderBottom: "0px",
              paddingTop: "4px",
              paddingBottom: "4px",
            },
          }}
          value={searchParams?.filterStatus || "CURRENT"}
          onChange={handleSearchTypeChange}
        >
          <MenuItem value={"CURRENT"}>ALL</MenuItem>

          {searchDropdownOptions[
            value === "0" ? "lead" : "surveyLead"
          ]?.map((item: any) => (
            <MenuItem
              key={item}
              value={item === "ESTIMATE" ? "ESTIMATES" : item}
            >
              {item}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>

      <StyledInputBase
        placeholder="Search a Lead"
        inputProps={{ "aria-label": "search a Lead" }}
        value={searchValue}
        onChange={handleSearchKeyChange}
      />

      {searchParams?.searchString && (
        <ClearOutlined
          onClick={hideHandler}
          sx={{
            position: "absolute",
            right: "-4px",
            padding: "9px",
            width: "16px",
            opacity: "0.5",
            cursor: "pointer",
          }}
        />
      )}
    </SearchBar>
  ) : null;
}